import {
  AddShoppingCart as AddToCartIcon,
  ArrowBack as BackIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Store as StoreIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Rating,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchSupplierDetails,
  fetchSupplierProducts,
} from "../../../application/actions/supplierActions";
import {
  selectSelectedSupplierData,
  selectSelectedSupplierLoading,
  selectSupplierProductsData,
  selectSupplierProductsLoading,
} from "../../../application/selectors/suppliers";

const SupplierDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const supplier = useSelector(selectSelectedSupplierData);
  const supplierLoading = useSelector(selectSelectedSupplierLoading);
  const products = useSelector(selectSupplierProductsData);
  const productsLoading = useSelector(selectSupplierProductsLoading);

  // Local State
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (id) {
      dispatch(fetchSupplierDetails(id));
      dispatch(fetchSupplierProducts({ supplierId: id, params: {} }));
    }
  }, [id, dispatch]);

  const handleBackToList = () => {
    navigate("/suppliers");
  };

  const handleProductSearch = () => {
    dispatch(
      fetchSupplierProducts({
        supplierId: id,
        params: { searchTerm },
      })
    );
  };

  const handleAddToCart = (product) => {
    // TODO: Implement add to cart functionality
    console.log("Add to cart:", product);
  };

  const filteredProducts = products?.filter((product) =>
    searchTerm
      ? product.name.toLowerCase().includes(searchTerm.toLowerCase())
      : true
  );

  if (supplierLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!supplier) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          تأمین‌کننده مورد نظر یافت نشد
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          بازگشت به لیست تأمین‌کنندگان
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBackToList}
          sx={{ mb: 2 }}
        >
          بازگشت به لیست
        </Button>
      </Box>

      {/* Supplier Info Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={2}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: "primary.main",
                fontSize: 48,
              }}
            >
              <StoreIcon fontSize="inherit" />
            </Avatar>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="h4" sx={{ mr: 2 }}>
                {supplier.companyName}
              </Typography>
              {supplier.isVerified && (
                <Chip
                  icon={<VerifiedIcon />}
                  label="تأیید شده"
                  color="success"
                />
              )}
            </Box>

            {supplier.rating && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Rating value={supplier.rating} readOnly sx={{ mr: 1 }} />
                <Typography variant="body1">
                  ({supplier.rating.toFixed(1)})
                </Typography>
              </Box>
            )}

            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {supplier.description}
            </Typography>

            <Grid container spacing={2}>
              {supplier.phone && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PhoneIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="body2">{supplier.phone}</Typography>
                  </Box>
                </Grid>
              )}

              {supplier.email && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <EmailIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="body2">{supplier.email}</Typography>
                  </Box>
                </Grid>
              )}

              {supplier.city && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LocationIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="body2">{supplier.city}</Typography>
                  </Box>
                </Grid>
              )}

              {supplier.category && (
                <Grid item xs={12} md={6}>
                  <Chip label={supplier.category} variant="outlined" />
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ backgroundColor: "primary.light" }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {supplier.productsCount || 0}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  محصول موجود
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Products Section */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5">محصولات</Typography>

          <TextField
            placeholder="جستجوی محصول..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleProductSearch();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {productsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredProducts && filteredProducts.length > 0 ? (
          <Grid container spacing={3}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={product.imageUrl || "/default-product.jpg"}
                    alt={product.name}
                    sx={{ objectFit: "cover" }}
                  />

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom noWrap>
                      {product.name}
                    </Typography>

                    {product.brand && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        برند: {product.brand}
                      </Typography>
                    )}

                    {product.category && (
                      <Chip
                        label={product.category}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {product.description}
                    </Typography>

                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {product.price?.toLocaleString("fa-IR")} ریال
                    </Typography>

                    {product.isAvailable !== undefined && (
                      <Chip
                        label={product.isAvailable ? "موجود" : "ناموجود"}
                        color={product.isAvailable ? "success" : "error"}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>

                  <CardActions>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddToCartIcon />}
                      onClick={() => handleAddToCart(product)}
                      disabled={!product.isAvailable}
                    >
                      افزودن به سبد
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              محصولی یافت نشد
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SupplierDetails;
